/**
 * releaseNotes.service.ts — Layer 3: Release Notes Domain
 *
 * Global Admin authoring/publishing, plus the end-user "what's new" modal.
 */

import {
  getRequest,
  postRequest,
  patchRequest,
  deleteRequest,
  postFormData,
} from './http.service';
import {
  URL_RELEASE_NOTES,
  URL_RELEASE_NOTES_LIST,
  URL_RELEASE_NOTE_BY_ID,
  URL_RELEASE_NOTE_PUBLISH,
  URL_RELEASE_NOTE_RESEND,
  URL_RELEASE_NOTE_DISMISS,
  URL_RELEASE_NOTES_PENDING,
  URL_RELEASE_NOTES_AUDIENCE,
  URL_RELEASE_NOTE_IMAGES,
} from './url.service';

export type ReleaseNoteStatus = 'draft' | 'scheduled' | 'published' | 'failed';
export type ReleaseNoteAudienceMode = 'all' | 'orgs' | 'users';

export interface ReleaseNoteAudience {
  mode: ReleaseNoteAudienceMode;
  orgIds: string[];
  userIds: string[];
}

export interface ReleaseNote {
  _id: string;
  title: string;
  version: string | null;
  body: string;
  summary: string | null;
  status: ReleaseNoteStatus;
  audience: ReleaseNoteAudience;
  sendEmail: boolean;
  showModal: boolean;
  publishAt: string | null;
  publishedAt: string | null;
  createdBy: string | null;
  sentCount: number;
  failedCount: number;
  failedRecipients: string[];
  lastSentAt: string | null;
  sendError: string | null;
  createdAt: string;
  updatedAt: string;
}

/** What an end user sees in the modal — no audience or send internals. */
export interface PendingReleaseNote {
  _id: string;
  title: string;
  version: string | null;
  body: string;
  summary: string | null;
  publishedAt: string | null;
}

export interface ReleaseNotePayload {
  title: string;
  version?: string;
  body: string;
  summary?: string;
  audience: { mode: ReleaseNoteAudienceMode; orgIds?: string[]; userIds?: string[] };
  sendEmail: boolean;
  showModal: boolean;
  publishAt?: string | null;
  // Index signature so this satisfies the request helpers' body type.
  [key: string]: unknown;
}

interface ListResponse {
  success: boolean;
  data: ReleaseNote[];
  pagination: { page: number; limit: number; total: number };
}

interface SingleResponse {
  success: boolean;
  data: ReleaseNote;
}

// ── Global admin ────────────────────────────────────────────────────────

export const fetchReleaseNotes = (page: number, limit: number, status?: string) =>
  getRequest<ListResponse>(URL_RELEASE_NOTES_LIST(page, limit, status));

export const fetchReleaseNote = (id: string) =>
  getRequest<SingleResponse>(URL_RELEASE_NOTE_BY_ID(id));

export const createReleaseNote = (payload: ReleaseNotePayload) =>
  postRequest<SingleResponse>(URL_RELEASE_NOTES, payload);

export const updateReleaseNote = (id: string, payload: Partial<ReleaseNotePayload>) =>
  patchRequest<SingleResponse>(URL_RELEASE_NOTE_BY_ID(id), payload);

export const deleteReleaseNote = (id: string) =>
  deleteRequest<{ success: boolean }>(URL_RELEASE_NOTE_BY_ID(id));

/** Publish immediately, ignoring any schedule already set. */
export const publishReleaseNote = (id: string) =>
  postRequest<SingleResponse>(URL_RELEASE_NOTE_PUBLISH(id), {});

/**
 * Re-run the email send. `failedOnly` retries just the addresses that failed
 * last time; otherwise the whole current audience is emailed again.
 */
export const resendReleaseNote = (id: string, failedOnly = false) =>
  postRequest<SingleResponse>(URL_RELEASE_NOTE_RESEND(id), { failedOnly });

/** How many active users the current audience selection resolves to. */
export const previewAudienceSize = (audience: ReleaseNotePayload['audience']) =>
  postRequest<{ success: boolean; data: { count: number } }>(
    URL_RELEASE_NOTES_AUDIENCE,
    { audience },
  );

/**
 * Upload a pasted/dropped image and get back a permanently public URL.
 *
 * The URL is deliberately unauthenticated — it gets embedded in the email, where
 * the recipient's mail client sends no token and a proxied URL would 401.
 */
export const uploadReleaseNoteImage = (file: File) => {
  const formData = new FormData();
  formData.append('image', file);
  return postFormData<{ success: boolean; data: { url: string; name: string } }>(
    URL_RELEASE_NOTE_IMAGES,
    formData,
  );
};

// ── End user ────────────────────────────────────────────────────────────

export const fetchPendingReleaseNote = () =>
  getRequest<{ success: boolean; data: PendingReleaseNote | null }>(
    URL_RELEASE_NOTES_PENDING,
  );

export const dismissReleaseNote = (id: string) =>
  postRequest<{ success: boolean }>(URL_RELEASE_NOTE_DISMISS(id), {});
